import { createAction, props } from '@ngrx/store';
import { PageableModel, ParamsModel, VentilationParameter } from '@mona/models';
import { EntriesInterval } from '@mona/shared/date';

/**
 * Ventilation actions
 */
export class VentilationAction {
    static setIntervalAction = createAction(
        'ENCOUNTER:SET_VENTILATION_INTERVAL',
        props<{ interval: EntriesInterval }>(),
    );

    static setLoadingAction = createAction('ENCOUNTER:SET_VENTILATION_LOADING', props<{ isLoading: boolean }>());

    static loadVentilationParameters = createAction(
        'ENCOUNTER:LOAD_VENTILATION_PARAMETERS_PAGINATED',
        props<{ params?: ParamsModel; url?: string }>(),
    );

    static loadVentilationParametersSuccess = createAction(
        'ENCOUNTER:LOAD_VENTILATION_PARAMETERS_PAGINATED_SUCCESS',
        props<PageableModel<VentilationParameter>>(),
    );

    static loadVentilationParametersFailed = createAction(
        'ENCOUNTER:LOAD_VENTILATION_PARAMETERS_PAGINATED_FAILED',
        props<{ error: unknown }>(),
    );

    /**
     * Emitted when an in-flight `loadVentilationParameters` is aborted because the selected
     * encounter was cleared mid-fetch (typical during logout/login or rapid encounter switch).
     * Lets the reducer drop the `isLoading: true` flag instead of leaving the ventilation
     * table's `ui-card` loading-overlay covering the cell-edit save buttons forever.
     *
     * Same bug shape as the MD-9750 stuck-spinner family already fixed for daily-goals
     * and procedure-prescriptions: the previous `takeUntil(isEmptyEncounterId)`
     * silently completed the inner observable without dispatching any terminal action,
     * so `loadVentilationParameters` left `isLoading: true` sticky in the reducer and
     * the `[uiLoadingOverlay]` on the `ventilation-table` mat-card buried any
     * subsequent cell-edit save click. Customer reproducer from a hospital
     * terminal: "save eventually went through but took some time"; the
     * workaround the colleague hit was the disconnect → discard → cancel-rfid
     * sequence, which dispatches a separate set of state-resetting actions that
     * finally cleared the stuck flag.
     */
    static loadVentilationParametersCancelled = createAction(
        'ENCOUNTER:LOAD_VENTILATION_PARAMETERS_PAGINATED_CANCELLED',
        props<{ reason: string }>(),
    );

    static removeVentilationParameters = createAction(
        'ENCOUNTER:REMOVE_VENTILATION_PARAMETERS',
        props<{ ids: string[] }>(),
    );

    static upsertVentilationParameters = createAction(
        'ENCOUNTER:UPSERT_VENTILATION_PARAMETERS',
        props<{ data: VentilationParameter[] }>(),
    );

    static addParameterChanges = createAction(
        'ENCOUNTER:ADD_VENTILATION_PARAMETER_CHANGES',
        props<{ toUpdateEntities: VentilationParameter[]; toRemoveIds: string[] }>(),
    );

    static clearVentilationData = createAction('ENCOUNTER:CLEAR_VENTILATION_PARAMETERS');
}
